import { useCallback, useEffect, useState } from 'react';
import { API_URLS } from '../constants/config';
import { matchPlayPhase } from './matchesApi';
import { authorizedPost } from './stadiumsApi';
import store from './store';

// Состояние онлайн-матча (таймер, заявка, расстановка) живёт в localStorage по id матча,
// поэтому переживает закрытие окна онлайна и перезагрузку страницы.
// Сервер знает только метки начала таймов — паузы и ручные правки таймера хранятся тут.

const KEY_PREFIX = 'afl_online_match_';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const RUNNING_STATUSES = ['half1', 'half2', 'extra1', 'extra2'];
const RUNNING_TO_PAUSED = { half1: 'paused1', half2: 'paused2', extra1: 'paused_extra1', extra2: 'paused_extra2' };
const PAUSED_TO_RUNNING = { paused1: 'half1', paused2: 'half2', paused_extra1: 'extra1', paused_extra2: 'extra2' };

// Статус таймера → фаза матча на сервере (matchPlayPhase).
const STATUS_PHASES = {
  idle: ['not_started'],
  half1: ['first_half'],
  paused1: ['first_half'],
  halftime: ['half_time'],
  half2: ['second_half'],
  paused2: ['second_half'],
  extra_break: ['extra_break'],
  extra1: ['extra_first'],
  paused_extra1: ['extra_first'],
  extra_halftime: ['extra_timeout'],
  extra2: ['extra_second'],
  paused_extra2: ['extra_second'],
  ended: ['finished', 'extra_ended', 'tech_defeat'],
};

const STATUS_LABEL_KEYS = {
  idle: 'olmWaiting',
  half1: 'olmHalf1',
  paused1: 'olmHalf1Paused',
  halftime: 'halfTime',
  half2: 'olmHalf2',
  paused2: 'olmHalf2Paused',
  extra_break: 'extraBreak',
  extra1: 'olmExtra1',
  paused_extra1: 'olmExtra1Paused',
  extra_halftime: 'extraHalfTime',
  extra2: 'olmExtra2',
  paused_extra2: 'olmExtra2Paused',
  ended: 'olmMatchEnded',
};

export const onlineStatusLabelKey = (status) => STATUS_LABEL_KEYS[status] || '';

const storageKey = (matchId) => `${KEY_PREFIX}${matchId}`;
const listeners = new Set();

export const loadOnlineMatch = (matchId) => {
  if (matchId == null) return null;
  try {
    const raw = localStorage.getItem(storageKey(matchId));
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const pruneOld = () => {
  try {
    const now = Date.now();
    Object.keys(localStorage)
      .filter((key) => key.startsWith(KEY_PREFIX))
      .forEach((key) => {
        try {
          const saved = JSON.parse(localStorage.getItem(key));
          if (!saved?.updatedAt || now - saved.updatedAt > MAX_AGE_MS) localStorage.removeItem(key);
        } catch (error) {
          localStorage.removeItem(key);
        }
      });
  } catch (error) { /* ignore */ }
};

const notify = (matchId, state) => listeners.forEach((listener) => listener(matchId, state));

/** Слить patch в сохранённое состояние матча. Без изменений — ничего не пишет и не оповещает. */
export const saveOnlineMatch = (matchId, patch) => {
  if (matchId == null) return;
  const prev = loadOnlineMatch(matchId) || {};
  const { updatedAt, ...prevData } = prev;
  const nextData = { ...prevData, ...patch };
  if (JSON.stringify(prevData) === JSON.stringify(nextData)) return;
  const next = { ...nextData, updatedAt: Date.now() };
  try {
    localStorage.setItem(storageKey(matchId), JSON.stringify(next));
  } catch (error) { /* ignore */ }
  if (!updatedAt) pruneOld();
  notify(matchId, next);
};

export const subscribeOnlineMatch = (matchId, listener) => {
  const own = (id, state) => { if (String(id) === String(matchId)) listener(state); };
  // Другая вкладка с этим же матчем.
  const onStorage = (event) => {
    if (event.key === storageKey(matchId)) listener(loadOnlineMatch(matchId));
  };
  listeners.add(own);
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(own);
    window.removeEventListener('storage', onStorage);
  };
};

// ─── Таймер: { status, base (сек, дробные), startedAt (ms, только пока идёт) } ───

export const isClockRunning = (clock) => Boolean(clock && RUNNING_STATUSES.includes(clock.status) && clock.startedAt);

export const clockSeconds = (clock, now = Date.now()) => {
  if (!clock) return 0;
  const base = Number(clock.base) || 0;
  if (!isClockRunning(clock)) return Math.max(0, base);
  return Math.max(0, base + (now - clock.startedAt) / 1000);
};

export const makeClock = (status, seconds = 0) => ({
  status,
  base: Math.max(0, Number(seconds) || 0),
  startedAt: RUNNING_STATUSES.includes(status) ? Date.now() : null,
});

export const pauseClock = (clock) => (
  clock && RUNNING_TO_PAUSED[clock.status]
    ? makeClock(RUNNING_TO_PAUSED[clock.status], clockSeconds(clock))
    : clock
);

export const resumeClock = (clock) => (
  clock && PAUSED_TO_RUNNING[clock.status]
    ? makeClock(PAUSED_TO_RUNNING[clock.status], clockSeconds(clock))
    : clock
);

/** Выставить время, не меняя статус (если таймер шёл — продолжит с нового значения). */
export const setClockSeconds = (clock, seconds) => makeClock(clock?.status || 'idle', seconds);

export const canPauseClock = (clock) => Boolean(clock && RUNNING_TO_PAUSED[clock.status]);
export const canResumeClock = (clock) => Boolean(clock && PAUSED_TO_RUNNING[clock.status]);

/** Онлайн идёт: матч начат и не закончен. */
export const isClockLive = (clock) => Boolean(clock && clock.status !== 'idle' && clock.status !== 'ended' && STATUS_PHASES[clock.status]);

/** Сохранённый таймер годится, только если совпадает с фазой матча на сервере. */
export const clockMatchesServer = (clock, match) => {
  if (!clock || !match) return false;
  const phases = STATUS_PHASES[clock.status];
  return Boolean(phases && phases.includes(matchPlayPhase(match)));
};

export const splitSeconds = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  return { minute: Math.floor(total / 60), second: total % 60 };
};

export const formatClock = (seconds) => {
  const { minute, second } = splitSeconds(seconds);
  return `${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
};

/**
 * Таймер онлайна по матчу: синхронизирован между окном онлайна, карточкой матча и вкладками.
 * tick=false — без ежесекундной перерисовки (только смена статуса/правки); секунды тогда брать через clockSeconds(clock).
 */
export const useOnlineClock = (matchId, { tick = true } = {}) => {
  const [clock, setClockState] = useState(() => loadOnlineMatch(matchId)?.clock || null);
  const [, setTick] = useState(0);

  useEffect(() => {
    setClockState(loadOnlineMatch(matchId)?.clock || null);
    if (matchId == null) return undefined;
    return subscribeOnlineMatch(matchId, (state) => setClockState(state?.clock || null));
  }, [matchId]);

  const running = tick && isClockRunning(clock);
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setTick((value) => value + 1), 500);
    return () => clearInterval(id);
  }, [running]);

  // sync=false — только локально (восстановление с сервера, сброс), иначе отправляем на сервер.
  const setClock = useCallback((next, { sync = true } = {}) => {
    const current = loadOnlineMatch(matchId)?.clock || null;
    const value = typeof next === 'function' ? next(current) : next;
    saveOnlineMatch(matchId, { clock: value });
    if (sync && value && value !== current) pushClockToServer(matchId, value);
  }, [matchId]);

  return { clock, seconds: Math.floor(clockSeconds(clock)), setClock };
};

// ─── Номер тайма события по этапу матча, а не по минуте ───
// По минуте нельзя: 30:15 во 2-м тайме (тайм 30 мин) — это 2-й тайм, а 31' в 1-м тайме — всё ещё 1-й.
const STATUS_TIME_NUMBER = {
  half1: 1,
  paused1: 1,
  halftime: 1,
  half2: 2,
  paused2: 2,
  extra_break: 2,
  extra1: 3,
  paused_extra1: 3,
  extra_halftime: 3,
  extra2: 4,
  paused_extra2: 4,
};

export const timeNumberForStatus = (status) => STATUS_TIME_NUMBER[status] || null;

/** Подсказка времени для формы события: минута/секунда с таймера + тайм по этапу. */
export const clockEventHint = (clock) => {
  if (!isClockLive(clock)) return null;
  const { minute, second } = splitSeconds(clockSeconds(clock));
  return { minute, second, time_number: timeNumberForStatus(clock.status) };
};

/** Тайм из подсказки, если время в форме не правили руками; иначе null — считать по минуте. */
export const hintedTimeNumber = (hint, minute, second) => {
  if (!hint || !hint.time_number) return null;
  if (Number(minute) !== Number(hint.minute) || Number(second || 0) !== Number(hint.second || 0)) return null;
  return hint.time_number;
};

// ─── Этап матча на сервере → статус таймера ───
const PHASE_STATUS = {
  not_started: ['idle', 'idle'],
  first_half: ['half1', 'paused1'],
  half_time: ['halftime', 'halftime'],
  second_half: ['half2', 'paused2'],
  extra_break: ['extra_break', 'extra_break'],
  extra_first: ['extra1', 'paused_extra1'],
  extra_timeout: ['extra_halftime', 'extra_halftime'],
  extra_second: ['extra2', 'paused_extra2'],
  finished: ['ended', 'ended'],
  extra_ended: ['ended', 'ended'],
  tech_defeat: ['ended', 'ended'],
};

export const statusForPhase = (phase, running) => {
  const pair = PHASE_STATUS[phase];
  return pair ? pair[running ? 0 : 1] : null;
};

/** Таймер для нового этапа матча (кнопки этапов в пульте табло). */
export const clockForPhase = (phase, { halfTime = 30, extraHalfMin = 15, currentSeconds = 0 } = {}) => {
  const half = (Number(halfTime) || 30) * 60;
  const extra = (Number(extraHalfMin) || 15) * 60;
  switch (phase) {
    case 'not_started': return makeClock('idle', 0);
    case 'first_half': return makeClock('half1', 0);
    case 'half_time': return makeClock('halftime', Math.max(currentSeconds, half));
    case 'second_half': return makeClock('half2', half);
    case 'extra_break': return makeClock('extra_break', half * 2);
    case 'extra_first': return makeClock('extra1', half * 2);
    case 'extra_timeout': return makeClock('extra_halftime', half * 2 + extra);
    case 'extra_second': return makeClock('extra2', half * 2 + extra);
    case 'finished':
    case 'extra_ended':
    case 'tech_defeat':
      return makeClock('ended', currentSeconds);
    default: return null;
  }
};

// ─── Синхронизация с сервером (POST /matches/setAdminMatchClock) ───
// 404 больше не «убивает» sync на всю сессию (рут могли задеплоить после первой попытки).
let serverClockRetryAfter = 0;

export const pushClockToServer = async (matchId, clock) => {
  if (matchId == null || !clock || clock.status === 'idle') return;
  if (Date.now() < serverClockRetryAfter) return;
  try {
    const response = await authorizedPost(store.dispatch, API_URLS.setAdminMatchClock, {
      match_id: Number(matchId),
      running: isClockRunning(clock),
      seconds: Math.round(clockSeconds(clock) * 10) / 10,
    });
    serverClockRetryAfter = 0;
    const live = response?.data?.live_clock || response?.data?.data?.live_clock;
    // Запоминаем версию, чтобы не принять собственный ответ как «чужое» изменение.
    if (live?.updated_at) saveOnlineMatch(matchId, { clockServerUpdatedAt: live.updated_at });
  } catch (error) {
    if (error?.response?.status === 404) {
      serverClockRetryAfter = Date.now() + 30_000;
    }
  }
};

/**
 * Принять таймер с сервера (match.live_clock), если он новее того, что уже принят.
 * Так паузы с другого устройства / пульта табло доезжают сюда.
 */
export const adoptServerClock = (matchId, match) => {
  const live = match?.live_clock;
  if (!live || matchId == null) return false;
  serverClockRetryAfter = 0;
  const saved = loadOnlineMatch(matchId);
  if (live.updated_at && saved?.clockServerUpdatedAt === live.updated_at) return false;
  const status = statusForPhase(matchPlayPhase(match), Boolean(live.running));
  if (!status) return false;
  // sync=false путь: пишем напрямую, без повторного POST своего же состояния.
  saveOnlineMatch(matchId, {
    clock: makeClock(status, Number(live.seconds) || 0),
    clockServerUpdatedAt: live.updated_at || null,
  });
  return true;
};

/** Пробел — пауза / продолжить. Не срабатывает, когда фокус в поле ввода. */
export const useClockHotkey = (enabled, onToggle) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (event) => {
      if (event.code !== 'Space' && event.key !== ' ') return;
      if (event.defaultPrevented || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
      // Сфокусированная кнопка иначе ещё и «нажмётся» пробелом.
      if (tag === 'BUTTON' && typeof target.blur === 'function') target.blur();
      event.preventDefault();
      onToggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, onToggle]);
};

/** Пауза ↔ продолжить. Возвращает новый таймер или тот же, если в этом этапе пауза не применима. */
export const toggleClock = (clock) => {
  if (canPauseClock(clock)) return pauseClock(clock);
  if (canResumeClock(clock)) return resumeClock(clock);
  return clock;
};
